import React, { useState } from "react";
import { ChevronDown, ChevronUp, ArrowRight, Plus, Train, Footprints, Moon, Zap } from "lucide-react";
import { fmtDuration, fmtTime, modeLabel, modeClass, estimatePriceEur } from "../lib/api";

function modeIcon(mode) {
  if (!mode) return <Train className="h-3.5 w-3.5" />;
  if (mode === "WALK") return <Footprints className="h-3.5 w-3.5" />;
  if (mode.includes("HIGHSPEED")) return <Zap className="h-3.5 w-3.5" />;
  if (mode.includes("NIGHT")) return <Moon className="h-3.5 w-3.5" />;
  return <Train className="h-3.5 w-3.5" />;
}

export default function ItineraryCard({ itin, fromName, toName, onAddToRoute, index }) {
  const [open, setOpen] = useState(false);
  const transitLegs = itin.legs.filter((l) => l.mode !== "WALK");
  const firstTransit = transitLegs[0];
  const lastTransit = transitLegs[transitLegs.length - 1];
  const operators = [...new Set(transitLegs.map((l) => l.route_short_name).filter(Boolean))];
  const price = itin.legs.reduce((sum, l) => sum + (l.mode === "WALK" ? 0 : estimatePriceEur(l.duration, l.mode)), 0);

  const handleAdd = () => {
    onAddToRoute({
      from_city: fromName,
      to_city: toName,
      duration_seconds: itin.duration,
      est_price_eur: price,
      mode: firstTransit?.mode || transitLegs[0]?.mode,
      start_time: itin.start_time,
      end_time: itin.end_time,
      transfers: itin.transfers,
      legs: itin.legs,
    });
  };

  return (
    <div
      data-testid={`itinerary-card-${index}`}
      className="iq-card overflow-hidden hover:border-gold/40 transition-colors animate-fade-up"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="grid grid-cols-12 gap-4 p-5">
        {/* Times */}
        <div className="col-span-3 md:col-span-2">
          <div className="font-display text-3xl md:text-4xl text-cream tracking-tight">{fmtTime(itin.start_time)}</div>
          <div className="text-xs text-cream-dim/70 mt-1 truncate">{firstTransit?.from?.name || fromName}</div>
        </div>

        {/* Connector */}
        <div className="col-span-6 md:col-span-7 flex flex-col items-center justify-center">
          <div className="text-xs uppercase tracking-[0.2em] text-gold/80">{fmtDuration(itin.duration)}</div>
          <div className="w-full flex items-center gap-2 my-2">
            <div className="h-px flex-1 bg-panel-border" />
            <ArrowRight className="h-4 w-4 text-gold" />
            <div className="h-px flex-1 bg-panel-border" />
          </div>
          <div className="text-[11px] text-cream-dim/70">
            {itin.transfers} {itin.transfers === 1 ? "change" : "changes"}
            {operators.length > 0 && <> · {operators.slice(0, 3).join(", ")}</>}
          </div>
        </div>

        {/* End */}
        <div className="col-span-3 text-right">
          <div className="font-display text-3xl md:text-4xl text-cream tracking-tight">{fmtTime(itin.end_time)}</div>
          <div className="text-xs text-cream-dim/70 mt-1 truncate">{lastTransit?.to?.name || toName}</div>
        </div>
      </div>

      {/* Action row */}
      <div className="border-t border-panel-border bg-ink/40 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {transitLegs.slice(0, 4).map((l, i) => (
            <span key={`${l.start_time || ""}-${l.route_short_name || l.mode}-${i}`} className={`mode-pill ${modeClass(l.mode)} inline-flex items-center gap-1.5`}>
              {modeIcon(l.mode)}
              {l.route_short_name || modeLabel(l.mode)}
            </span>
          ))}
          {transitLegs.length > 4 && (
            <span className="text-cream-dim/70 text-xs">+{transitLegs.length - 4}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="iq-overline">from</div>
            <div className="font-display text-xl text-gold">€{price}</div>
          </div>
          <button
            data-testid={`itinerary-details-btn-${index}`}
            onClick={() => setOpen((o) => !o)}
            className="iq-btn-ghost text-xs"
          >
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            Details
          </button>
          <button
            data-testid={`itinerary-add-btn-${index}`}
            onClick={handleAdd}
            className="iq-btn-primary text-sm py-2 px-4"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-panel-border p-5 bg-ink/20 space-y-3">
          {itin.legs.map((l, i) => (
            <div key={`${l.start_time || ""}-${l.mode || ""}-${i}`} className="flex items-start gap-4">
              <div className="w-16 shrink-0 text-right">
                <div className="font-display text-lg text-cream">{fmtTime(l.start_time)}</div>
                <div className="text-[10px] text-cream-dim/60">{fmtDuration(l.duration)}</div>
                <div className="font-display text-lg text-cream-dim mt-2">{fmtTime(l.end_time)}</div>
              </div>
              <div className="relative pl-4 flex-1 border-l border-panel-border pb-2">
                <span className={`absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full ${l.mode === "WALK" ? "bg-cream-dim/40" : "bg-gold"}`} />
                <div className="text-cream text-sm">{l.from?.name}{l.from?.track && <span className="text-cream-dim/70"> · Pl. {l.from.track}</span>}</div>
                <div className="my-1.5 flex items-center gap-2">
                  <span className={`mode-pill ${modeClass(l.mode)} inline-flex items-center gap-1.5`}>
                    {modeIcon(l.mode)}
                    {l.route_short_name || modeLabel(l.mode)}
                  </span>
                  {l.headsign && <span className="text-xs text-cream-dim/70 truncate">→ {l.headsign}</span>}
                </div>
                <div className="text-cream text-sm">{l.to?.name}{l.to?.track && <span className="text-cream-dim/70"> · Pl. {l.to.track}</span>}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
